<script lang="ts">
  import Card, { Content } from '@smui/card';
  import { createEventDispatcher } from 'svelte';
  import { getStr, isDirectionRTL, isMobile, isMobileWidth } from '../../../common/util';
  import Icon from '../../common/components/Icon.svelte';
  import {
    ensureFlixMediaDataAvailability,
    gtmForPdpSmallBox,
    hasDiscoverContent,
  } from '../services/pdp-data-services';
  import {
    flixMediaDataAvailable,
    isTTL24DataAvailable,
    pdpMoreInfoActiveTab,
    productImages,
    showPdpMoreInfoModal,
  } from '../store/pdp_store';

  export let extraProductGalleryTiles = [];
  export let parentComponent = 'mainGallery';
  export let showekySyndication;
  export let StyleClass = '';
  export let activeCode = '';
  export let showOnDesktop = false;
  export let data: any = {};

  const dispatch = createEventDispatcher();
  const isRTL = isDirectionRTL();

  let selectedCode;
  let image360View = [];

  const GALLERY_CODES = new Set(['360_VIEW']);

  const MOBILE_TAB_CONFIG = {
    DISCOVER: {
      contentId: 'discover-tab-content',
      activeTab: 'Discover',
      shouldShow: () => showDiscoverTab,
    },
    SPECIFICATION: {
      contentId: 'specifications-tab-content',
      activeTab: 'GeekyInfo',
      shouldShow: () => showGeekyInfoTab,
    },
    REVIEW: {
      contentId: 'reviews-tab-content',
      activeTab: 'Reviews',
      shouldShow: () => true,
    },
  };

  $: hasGroupedFeatures = Object.keys(data?.groupedfeatures || {}).length > 0;
  $: ensureFlixMediaDataAvailability(data);

  $: showDiscoverTab = hasDiscoverContent(data, $flixMediaDataAvailable, $isTTL24DataAvailable);
  $: showGeekyInfoTab = data?.productSpecifications != null || hasGroupedFeatures || data?.classifications != null;

  $: image360View = ($productImages || []).filter(({ type }) => type === 'set');

  $: displayTiles = parentComponent === 'productGallery' ? getGalleryTiles() : getMainMobileTiles(showDiscoverTab);

  function getGalleryTiles() {
    return [
      {
        code: 'PRODUCT_IMAGES',
        label: getStr('extra.product.images'),
        enabled: true,
        url: null,
        displayOrder: 1,
      },
      ...(extraProductGalleryTiles || []).filter(tile => GALLERY_CODES.has(tile.code)),
    ];
  }

  function getMainMobileTiles(discoverTabVisible) {
    if (!isMobileWidth()) {
      return extraProductGalleryTiles || [];
    }
    return (extraProductGalleryTiles || []).reduce((tiles, tile) => {
      const config = MOBILE_TAB_CONFIG[tile.code?.toUpperCase()];

      if (!config || (config.activeTab === 'Discover' ? !discoverTabVisible : !config.shouldShow())) {
        return tiles;
      }

      tiles.push({
        ...tile,
        ...config,
      });

      return tiles;
    }, []);
  }

  function onTileClick(code: string) {
    dispatch('tileClick', code);
  }

  function showPdpMoreInfo(activeTab: string) {
    $showPdpMoreInfoModal = true;
    $pdpMoreInfoActiveTab = activeTab;
  }

  function showActiveTile(tile) {
    if (tile.activeTab) {
      showPdpMoreInfo(tile.activeTab);
    }

    selectedCode = tile.code;
  }

  function gtmEvent(name: string) {
    gtmForPdpSmallBox(name);
  }

  function shouldHideTile(tile) {
    if (tile.code !== '360_VIEW') {
      return false;
    }

    const hasNo360Images = image360View.length === 0;

    return isMobileWidth() ? hasNo360Images : parentComponent === 'mainGallery' || hasNo360Images;
  }
</script>

<div class="pdp-small-box card-container {showOnDesktop || isMobile() ? '' : 'hide-on-desktop'} {StyleClass}">
  <div class="card-display" class:rtl={isRTL}>
    {#each displayTiles as data}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      {#if data?.code === 'AR' && showekySyndication}
        <div class="eky-syndication-container"></div>
      {:else}
        <div
          id={data.contentId}
          data-content-id={data.contentId}
          class:hidden={shouldHideTile(data)}
          class:selected={parentComponent === 'productGallery'
            ? activeCode
              ? data.code === activeCode
              : data.code === 'PRODUCT_IMAGES'
            : selectedCode === data.code}
          on:click={() => onTileClick(data.code)}
          on:click={() => gtmEvent(data.label)}
          class="card-display-inn"
        >
          <Card on:click={() => showActiveTile(data)}>
            {#if data['imageUrl'] !== undefined && data.imageUrl !== null}
              <img class="card-container-card-icon" src={data.imageUrl} alt={data.label} />
            {:else if data.code === 'PRODUCT_IMAGES'}
              <svelte:component this={Icon} glyph="icon-image" type="primary" desktopSize={16} />
            {/if}
            <Content>{data.label}</Content>
          </Card>
        </div>
      {/if}
    {/each}
  </div>
</div>

<style lang="scss">
  @import '../../common/styles/colors.new.scss';

  :global {
    .pdp-small-box {
      .card-display-inn {
        min-height: 36px;
        display: flex;
        .mdc-card {
          position: relative;
          border-radius: 10px;
          padding: 8px 12px;
          flex-direction: row;
          width: max-content;
          font-size: 14px;
          gap: 4px;
          align-items: center;
          box-shadow: 0px 4px 12px rgba(219, 227, 232, 0.6);
          cursor: pointer;
          display: flex;
          background: $primary-white;
          @include desktop-min-width {
            padding: 8px 16px;
            font-size: 14px;
            line-height: 16px;
            font-weight: 500;
          }
        }

        .smui-card__content {
          color: $primary-black;
          padding: 0px;
          text-align: left;
          font-size: 11px;
          font-weight: 500;
          line-height: 12px;
          letter-spacing: 0em;
          min-width: 39px;
        }

        @include desktop-max-width {
          min-height: inherit;
          font-weight: 400;
          .mdc-card {
            flex-direction: row;
            align-items: center;
            gap: 8px;
          }
          .smui-card__content {
            text-align: center;
          }
        }
        @include desktop-min-width {
          .smui-card__content {
            font-size: 14px;
            line-height: 16px;
            min-width: unset;
            padding-left: 4px;
          }
        }
      }
      &.mp_small-box-section {
        .card-display-inn {
          @include desktop-max-width {
            .mdc-card {
              flex-direction: row;
            }
          }
        }
      }
    }
  }
  .pdp-small-box {
    &.mp_small-box-section {
      .card-display-inn {
        width: inherit;
        min-width: inherit;
        min-height: inherit;
      }
    }
    &.card-container {
      @include desktop-max-width {
        border-radius: 0;
        margin-bottom: 0;
      }
    }
    .card-display {
      .eky-syndication-container {
        @include desktop-max-width {
          z-index: 1;
        }
      }
      display: flex;
      overflow: auto;
      justify-content: center;
      gap: 8px;
      padding: 16px 0;
      @include desktop-max-width {
        background: $neutral-light-04;
        justify-content: left;
        padding: 16px 15px;
      }

      @include desktop-min-width {
        gap: 12px;
        padding: 24px 48px;
        box-shadow: unset;
        justify-content: start;
      }

      &-inn {
        border-radius: 10px;
        &:not(.selected) {
          border: 1px solid $secondary-gray-02;
        }
        &.selected {
          border: 1px solid $icon-only-icon-blue;
        }
      }
    }
    .card-display {
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
    }
    .hidden {
      display: none;
    }

    .card-container {
      &-card {
        display: flex;

        &-icon {
          width: 16px;
          height: 16px;
          @include desktop-max-width {
            padding: 0;
            width: 20px;
            height: 20px;
          }
          text-align: center;
        }
      }
    }
  }
</style>
